// ============================================================================
// Mapa de sitios y rutas (Leaflet + OpenStreetMap), mismo patrón que
// pages/public/rides/RouteLeafletMap: carga perezosa de Leaflet y su CSS.
// Pinta puntos (bases o puntos de control) y, opcionalmente, la ruta que los
// une en orden. Sin llaves de API.
// ============================================================================

import { useEffect, useRef } from "react";
import type { Map as LMap, LayerGroup, LatLngTuple } from "leaflet";

export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  label: string;
  /** Color de relleno (hex). */
  color?: string;
  /** Número dentro del marcador (orden del punto de control). */
  order?: number;
}

interface Props {
  points: MapPoint[];
  /** Une los puntos con una línea (ruta de rondín), cerrando el circuito. */
  path?: boolean;
  pathColor?: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  className?: string;
}

type LeafletModule = typeof import("leaflet");

export default function SiteMap({ points, path = false, pathColor = "#14b8a6", selectedId = null, onSelect, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LMap | null>(null);
  const layerRef = useRef<LayerGroup | null>(null);
  const LRef = useRef<LeafletModule | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    let destroyed = false;
    let ro: ResizeObserver | null = null;
    import("leaflet").then((L) => {
      if (destroyed || !containerRef.current || mapRef.current) return;
      LRef.current = L;
      const map = L.map(containerRef.current, { center: [22.5, -101.5], zoom: 5, zoomControl: true, scrollWheelZoom: false, attributionControl: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      layerRef.current = L.layerGroup().addTo(map);
      ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(containerRef.current);
      draw();
    });
    return () => {
      destroyed = true;
      ro?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function draw() {
    const L = LRef.current;
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!L || !map || !layer) return;
    layer.clearLayers();
    const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    const latlngs: LatLngTuple[] = valid.map((p) => [p.lat, p.lng]);
    if (path && latlngs.length > 1) {
      L.polyline([...latlngs, latlngs[0]], { color: pathColor, weight: 4, opacity: 0.85, dashArray: "8,6" }).addTo(layer);
    }
    valid.forEach((p) => {
      const selected = p.id === selectedId;
      const color = p.color || pathColor;
      const html = `<div style="width:${selected ? 30 : 24}px;height:${selected ? 30 : 24}px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font:600 11px/1 system-ui,sans-serif">${p.order ?? ""}</div>`;
      const size = selected ? 30 : 24;
      const marker = L.marker([p.lat, p.lng], { icon: L.divIcon({ html, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2] }), title: p.label });
      marker.bindTooltip(p.label, { direction: "top", offset: [0, -size / 2] });
      marker.on("click", () => onSelectRef.current?.(p.id));
      marker.addTo(layer);
    });
    if (latlngs.length === 1) map.setView(latlngs[0], 16);
    else if (latlngs.length > 1) map.fitBounds(L.latLngBounds(latlngs), { padding: [36, 36], maxZoom: path ? 18 : 12 });
  }

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, path, pathColor, selectedId]);

  return <div ref={containerRef} className={className} style={{ minHeight: 220, zIndex: 0 }} />;
}
